import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input} from '@angular/core'
import {Backend} from "@generated-backend-types";
import {PrivateUploaderService} from "./services/private-uploader.service";
import {PrivateAttachmentsApiService} from "./services/private-attachments-api.service";
import {takeUntil} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {take} from "rxjs/operators";
import {BsModalService} from "ngx-bootstrap/modal";
import {AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {ConfirmDeleteModalComponent} from '../../confirm-delete-modal/confirm-delete-modal.component';
import {AbstractFileManagerComponent} from '../../abstract-file-manager.component';
import PrivateAttachment = Backend.App.Resources.Dashboard.Administration.PrivateAttachment;

@Component({
  selector: 'app-private-file-upload-field[privateAttachments][privateAttachmentAttachableId][privateAttachmentAttachableType][privateAttachmentType][privateAttachmentMeta]',
  templateUrl: './private-file-upload-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    TranslatePipe,
    AsyncPipe
  ],
  standalone: true
})
export class PrivateFileUploadFieldComponent extends AbstractFileManagerComponent {
  @Input() public layout: 'btn-first' | 'list-first' | 'btn-only' = 'list-first';
  @Input() public showUploadButton: boolean = true;
  @Input() public readonly = false;
  @Input() public btnTranslateKey = 'components.private_file_upload_card.button';

  public constructor(
    protected override cdr: ChangeDetectorRef,
    protected override privateUploadService: PrivateUploaderService,
    protected override toastr: ToastrService,
    protected override translate: TranslateService,
    private readonly privateAttachmentsApiService: PrivateAttachmentsApiService,
    private readonly bsModalService: BsModalService,
  ) {
    super(cdr, privateUploadService, toastr, translate);
  }

  public openUpload(): void {
    this.fileInput.nativeElement.click();
  }

  public remove(privateAttachmentToDelete: PrivateAttachment): void {
    if (
      !this.privateAttachmentType ||
      !this.privateAttachmentAttachableType ||
      !this.privateAttachmentAttachableId
    ) return;

    const index = this.privateAttachments.findIndex(att => att.id === privateAttachmentToDelete.id);

    this.bsModalService.show(ConfirmDeleteModalComponent, {
      initialState: {
        modelId: privateAttachmentToDelete.id,
        dialogTranslation: this.translate.instant('components.private_file_upload_card.confirm_title', {itemName: privateAttachmentToDelete.filename}),
        successTranslation: this.translate.instant('components.private_file_upload_card.delete.success', {itemName: privateAttachmentToDelete.filename}),
        errorTranslation: this.translate.instant('components.private_file_upload_card.delete.error', {itemName: this.itemName}),
        observable: this.privateAttachmentsApiService.delete(privateAttachmentToDelete.id),
      }
    }).content?.deleteSuccess.pipe(
      takeUntil(this.onDestroy$),
      take(1)
    ).subscribe(() => {
      if (index >= 0) {
        this.privateAttachments.splice(index, 1);
        this.checkUploadsAllowedState();
        this.cdr.markForCheck();
      }
    });
  }

  protected checkUploadsAllowedState(): void {
    const fileLimit = this.privateAttachmentMeta?.file_limits.find(limit => limit.attachment_type === this.privateAttachmentType);

    if (fileLimit?.max_files === undefined || fileLimit?.new_uploads_allowed === undefined) {
      this.uploadsAllowed$.next(false);
      return;
    }

    if (fileLimit.new_uploads_allowed === false) {
      this.uploadsAllowed$.next(false);
      return;
    }

    this.uploadsAllowed$.next(this.privateAttachments.length < fileLimit.max_files);
  }
}
